import { lazy, Suspense, useEffect } from 'react';

import { useUnit } from 'effector-react';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import AppAlert from '@m/components/AppAlert';
import { isProd } from '@m/config/FeatureFlags';
import { useHeapIdentifyUser } from '@m/hooks/useHeapIdentifyUser';
import { KeycloakService } from '@m/keycloak-service/KeycloakService';
import { hasRoles } from '@m/services/keycloak/iamRoles';
import { fetchEnums, fetchLPStates } from '@m/state/enum';
import { fontDefault } from '@m/styled/fonts';
import { RoleName } from '@m/types/api/iam/Iam';
import { userAnalytics } from '@m/utils/userAnalytics';
import { BreadCrumbs } from 'apps/admin/components/BreadCrumbs';
// #region import routes
import Navigation from 'apps/admin/components/Navigation/Navigation';
import CustomerRoutes from 'apps/admin/routes/CustomerRoutes';
import DevToolRoutes from 'apps/admin/routes/DevToolRoutes';
import EnforcementRoutes from 'apps/admin/routes/EnforcementRoutes';
import EnterpriseRoutes from 'apps/admin/routes/EnterpriseRoutes';
import EventsRoutes from 'apps/admin/routes/EventsRoutes';
import FeatureFlagRoutes from 'apps/admin/routes/FeatureFlagRoutes';
import LocationGroupRoutes from 'apps/admin/routes/LocationGroupRoutes';
import LocationRoutes from 'apps/admin/routes/LocationRoutes';
import ProspectSurveyRoutes from 'apps/admin/routes/ProspectingRoutes';
import ResourceGroupRoutes from 'apps/admin/routes/ResourceGroupRoutes';
import SiteSurveyRoutes from 'apps/admin/routes/SiteSurveyRoutes';
import UserRoutes from 'apps/admin/routes/UserRoutes';
import VisitRoutes from 'apps/admin/routes/VisitRoutes';
// #endregion import routes
import { getCurrentUser, authStore } from 'apps/admin/state/auth';

import {
  AntdOverrides,
  StyledRootLayout,
  StyledMainLayout,
  StyledContent,
  AlertBox,
} from './AdminApp.styled';
import ReportingRoutes from './routes/ReportingRoutes';
import { trackAdminUser } from './utils/heapOnboardTracking';

const CustomerReportingPrototype = lazy(
  () => import('apps/admin/containers/CustomerReportingPrototype/CustomerReportingContainer'),
);

const persistPathName = 'persist-path';

export default function Admin() {
  const history = useHistory();

  const [persistedPath, setPersistedPath] = useLocalStorage(persistPathName, '');

  const { pathname } = useLocation();
  const { authenticated, currentUser } = useUnit(authStore);

  const surveyRoutes = [
    '/location-survey',
    '/add-zone',
    '/add-lane',
    '/add-display',
    '/prospect-survey',
    '/scouted-location-survey',
  ];
  const hideNavbar = surveyRoutes.some((route) => pathname.includes(route));

  useEffect(() => {
    getCurrentUser().then((response) => {
      const data = {
        email: response?.data?.user?.email,
        permissionGroups: response?.data?.user?.permissionGroups,
      };

      let userData = response.data?.user;
      if (KeycloakService.isLoggedIn()) {
        userData = KeycloakService.getUserObject();
      }
      if (userData) {
        userAnalytics.setUser(userData);
      }
      trackAdminUser(data);
    });
  }, []);

  useEffect(() => {
    fetchEnums({ isAdmin: true });
    fetchLPStates();
  }, [authenticated]);

  useEffect(() => {
    if (persistedPath?.length) {
      history.replace(persistedPath);
      setPersistedPath('');
    } else if (pathname === '/login' || pathname === '/login-redirect' || pathname === '/') {
      // TODO: build dashboard as the default page
      history.replace('/customers');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, history, pathname]);

  useHeapIdentifyUser({
    authenticated,
    user: currentUser,
  });

  return (
    <StyledRootLayout hasSider>
      <AlertBox>
        <AppAlert />
      </AlertBox>
      <Navigation />
      <StyledMainLayout
        hidenavbar={hideNavbar.toString()}
        style={{
          fontFamily: fontDefault,
        }}
      >
        <StyledContent>
          <BreadCrumbs />
          {(hasRoles([RoleName.MANAGER]) && (
            <>
              <EnterpriseRoutes />
              <DevToolRoutes />
              <LocationGroupRoutes />
              <LocationRoutes />
              <UserRoutes />
              <CustomerRoutes />
              <EventsRoutes />
              <EnforcementRoutes />
              <FeatureFlagRoutes />
              <VisitRoutes />
              <SiteSurveyRoutes />
              <ResourceGroupRoutes />
              <ReportingRoutes />
            </>
          )) ||
            (hasRoles([RoleName.CUSTOMER_SUPPORT]) && (
              <>
                <EnterpriseRoutes /> {/* readonly route */}
                <LocationGroupRoutes /> {/* readonly route */}
                <LocationRoutes /> {/* readonly route */}
                <DevToolRoutes /> {/* unrestricted route */}
                <ResourceGroupRoutes /> {/* readonly route */}
                <CustomerRoutes /> {/* unrestricted route */}
                <EventsRoutes />
                <EnforcementRoutes /> {/* unrestricted route */}
                <VisitRoutes /> {/* unrestricted route */}
                <UserRoutes /> {/* Can only create or view other Customer Support users  */}
              </>
            ))}
          <ProspectSurveyRoutes /> {/* for specialist users  */}
          <Switch>
            {/* Prototype route - only available in non-production environments */}
            {!isProd && (
              <Route exact path="/reporting">
                <Suspense fallback={null}>
                  <CustomerReportingPrototype />
                </Suspense>
              </Route>
            )}
          </Switch>
        </StyledContent>
      </StyledMainLayout>
      {/* AntdOverrides is at the bottom so it has the most specificity */}
      <AntdOverrides />
    </StyledRootLayout>
  );
}
